import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { DateRangePicker } from 'react-date-range';
import Menu from '@mui/material/Menu';
import IconButton from '@mui/material/IconButton';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { TextFieldReadOnly } from '../../base/inputfield/TextFieldReadOnly';
import { BoxComponent } from '../../base/box/Box';
import { BaseButton } from '../../base/button/BaseButton';
import { SxProps, Theme } from '@mui/material';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

interface DateRangeProps {
  selectDateRange: { startDate: Dayjs | null; endDate: Dayjs | null };
  setSelectDateRange: (range: { startDate: Dayjs | null; endDate: Dayjs | null }) => void;
  dateFormat?: string;
  rangeColors?: string[];
  showMonthAndYearPickers?: boolean;
  sideBarRange?: boolean;
  months?: number;
  direction?: 'horizontal' | 'vertical';
  inputRanges?: any[];
  staticRanges?: any[];
  sx?: SxProps<Theme>;
}

export function DateRange({
  selectDateRange,
  setSelectDateRange,
  dateFormat = 'MMM DD, YYYY',
  rangeColors = ['#727AC4'],
  showMonthAndYearPickers = true,
  sideBarRange = false,
  months = 1,
  direction = 'horizontal',
  inputRanges = [],
  staticRanges = [],
  sx = { width: '300px' },
}: DateRangeProps) {
  const initialRange = {
    startDate: selectDateRange.startDate?.toDate() || new Date(),
    endDate: selectDateRange.endDate?.toDate() || new Date(),
    key: 'selection',
  };

  const [localDateRange, setLocalDateRange] = useState(initialRange);

  useEffect(() => {
    setLocalDateRange({
      startDate: selectDateRange.startDate?.toDate() || new Date(),
      endDate: selectDateRange.endDate?.toDate() || new Date(),
      key: 'selection',
    });
  }, [selectDateRange]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleDateChange = (ranges: any) => {
    const newStartDate = ranges.selection.startDate ? dayjs(ranges.selection.startDate) : null;
    const newEndDate = ranges.selection.endDate ? dayjs(ranges.selection.endDate) : null;

    const today = dayjs().endOf('day');
    if ((newStartDate && newStartDate.isAfter(today)) || (newEndDate && newEndDate.isAfter(today))) {
      return;
    }

    setLocalDateRange(ranges.selection); // update local state only
  };

  const handleConfirm = () => {
    const newStartDate = localDateRange.startDate ? dayjs(localDateRange.startDate) : null;
    const newEndDate = localDateRange.endDate ? dayjs(localDateRange.endDate) : null;

    setSelectDateRange({ startDate: newStartDate, endDate: newEndDate }); // propagate to parent
    closePopup();
  };

  const handleClear = () => {
    const cleared = {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    };
    setLocalDateRange(cleared); // reset to today's range, keep picker open
    setSelectDateRange({ startDate: null, endDate: null }); // propagate to parent
    closePopup();
  };

  const closePopup = () => {
    setTimeout(() => {
      setAnchorEl(null); // close menu
    }, 5);
  };

  const formattedDateRange =
    selectDateRange.startDate && selectDateRange.endDate
      ? selectDateRange.startDate.isSame(selectDateRange.endDate, 'day')
        ? `${selectDateRange.startDate.format(dateFormat)}`
        : `${selectDateRange.startDate.format(dateFormat)} - ${selectDateRange.endDate.format(dateFormat)}`
      : 'No range selected';

  return (
    <>
      <TextFieldReadOnly
        value={formattedDateRange}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          ...sx,
          border: selectDateRange.startDate && selectDateRange.endDate ? '2px solid #586FBC' : '1px solid #ccc',
          borderRadius: '12px',
          '&:hover': {
            border: selectDateRange.startDate && selectDateRange.endDate ? '2px solid #586FBC' : '1px solid #ccc',
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              border: 'none',
            },
            '&:hover fieldset': {
              border: 'none',
            },
            '&.Mui-focused fieldset': {
              border: 'none',
            },
          },
          '& .MuiOutlinedInput-root.Mui-focused': {
            boxShadow: 'none',
          },
        }}
        InputProps={{
          endAdornment: (
            <IconButton>
              <CalendarTodayIcon />
            </IconButton>
          ),
          readOnly: true,
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          style: {
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            border: '1px solid #E0E0E0',
          },
        }}
      >
        <BoxComponent className={sideBarRange ? 'date-range' : 'hide-date-range-sidebar'}>
          <DateRangePicker
            ranges={[localDateRange]}
            onChange={handleDateChange}
            moveRangeOnFirstSelection={false}
            editableDateInputs={true}
            maxDate={new Date()}
            showMonthAndYearPickers={showMonthAndYearPickers}
            inputRanges={inputRanges}
            staticRanges={staticRanges}
            months={months}
            direction={direction}
            rangeColors={rangeColors}
            dateDisplayFormat="MM/dd/yyyy"
          />
        </BoxComponent>
        <BoxComponent
          sx={{ display: 'flex', justifyContent: 'end', gap: 2, marginTop: '16px', padding: '0 16px 16px' }}
        >
          <BaseButton sx={{ display: 'flex' }} variant="outlined" onClick={handleClear}>
            Clear
          </BaseButton>
          <BaseButton sx={{ display: 'flex' }} variant="contained" onClick={handleConfirm}>
            Ok
          </BaseButton>
        </BoxComponent>
      </Menu>
    </>
  );
}
